/**
 * Notes API Client
 *
 * API functions for manager notes and meeting notes.
 */

import { apiRequest } from './api';

// Types
export type NoteType = 'general' | 'performance' | 'meeting' | 'development';

export interface ManagerNote {
  id: number;
  employeeId: number;
  managerId: number;
  type: NoteType;
  title: string | null;
  content: string;
  isPrivate: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface MeetingNote {
  id: number;
  evaluationId: number | null;
  employeeId: number;
  managerId: number;
  meetingDate: string;
  summary: string;
  discussedTopics: string | null;
  actionItems: string | null;
  employeeComments: string | null;
  isSharedWithEmployee: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateManagerNoteInput {
  employeeId: number;
  type?: NoteType;
  title?: string;
  content: string;
  isPrivate?: boolean;
}

export interface UpdateManagerNoteInput {
  type?: NoteType;
  title?: string;
  content?: string;
  isPrivate?: boolean;
}

export interface CreateMeetingNoteInput {
  evaluationId?: number;
  employeeId: number;
  meetingDate: string;
  summary: string;
  discussedTopics?: string[];
  actionItems?: string[];
  employeeComments?: string;
  isSharedWithEmployee?: boolean;
}

export interface UpdateMeetingNoteInput {
  summary?: string;
  discussedTopics?: string[];
  actionItems?: string[];
  employeeComments?: string;
  isSharedWithEmployee?: boolean;
}

export interface ManagerNotesResponse {
  data: ManagerNote[];
}

export interface MeetingNotesResponse {
  data: MeetingNote[];
}

// API functions
export const notesApi = {
  // Manager Notes

  /**
   * Get all manager notes (optionally filtered by employee)
   */
  async getManagerNotes(employeeId?: number): Promise<ManagerNotesResponse> {
    const params = employeeId ? `?employeeId=${employeeId}` : '';
    return apiRequest<ManagerNotesResponse>(`/notes/manager${params}`);
  },

  /**
   * Get single manager note
   */
  async getManagerNote(id: number): Promise<ManagerNote> {
    return apiRequest<ManagerNote>(`/notes/manager/${id}`);
  },

  /**
   * Create manager note
   */
  async createManagerNote(input: CreateManagerNoteInput): Promise<ManagerNote> {
    return apiRequest<ManagerNote>('/notes/manager', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  /**
   * Update manager note
   */
  async updateManagerNote(id: number, input: UpdateManagerNoteInput): Promise<ManagerNote> {
    return apiRequest<ManagerNote>(`/notes/manager/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },

  /**
   * Delete manager note
   */
  async deleteManagerNote(id: number): Promise<void> {
    await apiRequest(`/notes/manager/${id}`, { method: 'DELETE' });
  },

  // Meeting Notes

  /**
   * Get all meeting notes (optionally filtered by employee)
   */
  async getMeetingNotes(employeeId?: number): Promise<MeetingNotesResponse> {
    const params = employeeId ? `?employeeId=${employeeId}` : '';
    return apiRequest<MeetingNotesResponse>(`/notes/meeting${params}`);
  },

  /**
   * Get shared meeting notes for employee
   */
  async getSharedMeetingNotes(): Promise<MeetingNotesResponse> {
    return apiRequest<MeetingNotesResponse>('/notes/meeting/shared');
  },

  /**
   * Get single meeting note
   */
  async getMeetingNote(id: number): Promise<MeetingNote> {
    return apiRequest<MeetingNote>(`/notes/meeting/${id}`);
  },

  /**
   * Create meeting note
   */
  async createMeetingNote(input: CreateMeetingNoteInput): Promise<MeetingNote> {
    return apiRequest<MeetingNote>('/notes/meeting', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  /**
   * Update meeting note
   */
  async updateMeetingNote(id: number, input: UpdateMeetingNoteInput): Promise<MeetingNote> {
    return apiRequest<MeetingNote>(`/notes/meeting/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },

  /**
   * Delete meeting note
   */
  async deleteMeetingNote(id: number): Promise<void> {
    await apiRequest(`/notes/meeting/${id}`, { method: 'DELETE' });
  },
};
